
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getNotifications, markNotificationAsRead, markAllNotificationsAsRead } from '../api';
import { ENDPOINTS } from '../endpoints';

const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [activeEntity, setActiveEntity] = useState(null); // { type, id }

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await getNotifications();
      if (Array.isArray(data)) setNotifications(data);
    } catch {
      // Silently ignore (backend puede estar iniciando)
    }
  }, [isAuthenticated]);

  // Carga inicial al autenticarse
  useEffect(() => {
    if (isAuthenticated) fetchNotifications();
  }, [isAuthenticated, fetchNotifications]);

  // Suscripción SSE — el servidor empuja un evento al crear una notificación nueva.
  // El navegador reconecta automáticamente si la conexión se cae.
  // EventSource no soporta headers personalizados, por lo que el JWT va en la URL.
  useEffect(() => {
    if (!isAuthenticated) return;

    const token = localStorage.getItem('artemis_token');
    if (!token) return;

    const es = new EventSource(ENDPOINTS.NOTIFICATIONS_SSE(token));

    es.onmessage = () => {
      // Al recibir el evento, re-fetch la lista completa para mostrar la notificación
      fetchNotifications();
    };

    // onerror: el navegador intentará reconectar con backoff automático

    return () => es.close();
  }, [isAuthenticated, fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n)
      );
    } catch {
      // Silently ignore
    }
  };

  const markAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch {
      // Silently ignore
    }
  };

  const openEntity = (type, id) => setActiveEntity({ type, id });
  const closeEntity = () => setActiveEntity(null);

  const value = {
    notifications,
    unreadCount,
    activeEntity,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    openEntity,
    closeEntity,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
